import readlangClient from "readlangClient";
import * as ttsPolicy from "shared/ttsPolicy";
import {
  azureVoicesByLanguage,
  findAzureVoice,
  getDefaultAzureVoice,
  isAzureSupportedLanguage,
} from "shared/ttsVoiceCatalog";
import {
  boundaryOffsetToPosition,
  type BoundaryTextPart,
} from "./boundaryPositions";
import type {
  PlaybackHandle,
  SpeakOptions,
  TTSProvider,
  TTSVoice,
} from "./internalTypes";

const PROVIDER_ID = "azure";

// Polling interval for matching audio.currentTime against the boundary list.
// 50ms keeps highlighting responsive without flooding the main thread.
const BOUNDARY_POLL_MS = 50;

interface ServerBoundary {
  audioOffsetMs: number;
  durationMs: number;
  textOffset: number;
  wordLength: number;
  text: string;
  boundaryType: "WordBoundary" | "SentenceBoundary" | "PunctuationBoundary";
}

interface SynthesizeResponse {
  audioBase64: string;
  audioFormat: string;
  voiceId: string;
  boundaries: ServerBoundary[];
}

/**
 * Builds the audio element used for Azure playback. A single shared element
 * is reused so starting a new utterance cleanly stops the previous one.
 */
const getAudioElement = (): HTMLAudioElement => {
  let audio = document.getElementById(
    "azureTTSAudio"
  ) as HTMLAudioElement | null;
  if (!audio) {
    audio = document.createElement("audio");
    audio.id = "azureTTSAudio";
    audio.style.display = "none";
    document.body.appendChild(audio);
  }
  return audio;
};

let currentBoundaryPoll: number | undefined;
let currentAudio: HTMLAudioElement | undefined;

const clearBoundaryPoll = () => {
  if (currentBoundaryPoll !== undefined) {
    window.clearInterval(currentBoundaryPoll);
    currentBoundaryPoll = undefined;
  }
};

const stopCurrent = () => {
  clearBoundaryPoll();
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
    currentAudio.onended = null;
    currentAudio.onerror = null;
    currentAudio = undefined;
  }
};

const catalogVoiceToTTSVoice = (voice: {
  id: string;
  displayName: string;
  gender: "male" | "female";
}): TTSVoice => ({
  provider: PROVIDER_ID,
  id: voice.id,
  displayName: voice.displayName,
  gender: voice.gender,
  tag: "HD",
});

export const azureProvider: TTSProvider = {
  id: PROVIDER_ID,
  storage: "server",
  group: { id: "highQuality", label: "High quality voices", rank: 0 },
  minTierByFeature: {
    readAloud: ttsPolicy.azureRequiredTier("readAloud"),
    speakingMode: ttsPolicy.azureRequiredTier("speakingMode"),
  },

  supportsLanguage(language: string): boolean {
    return isAzureSupportedLanguage(language);
  },

  listVoices(language: string): TTSVoice[] {
    return (azureVoicesByLanguage[language] || []).map(catalogVoiceToTTSVoice);
  },

  getDefaultVoice(language: string): TTSVoice | undefined {
    const voice = getDefaultAzureVoice(language);
    return voice ? catalogVoiceToTTSVoice(voice) : undefined;
  },

  getVoice(language: string, voiceId: string): TTSVoice | undefined {
    const voice = findAzureVoice(language, voiceId);
    return voice ? catalogVoiceToTTSVoice(voice) : undefined;
  },

  getUpsellVoiceCount(language: string): number {
    return (azureVoicesByLanguage[language] || []).length;
  },

  async speak(opts: SpeakOptions): Promise<PlaybackHandle> {
    stopCurrent();

    const t0 = Date.now();
    const {
      feature,
      language,
      text,
      voice,
      rate,
      minStartDelayMs,
      onStart,
      onBoundary,
      onEnd,
      onError,
      abort,
    } = opts;

    // Allow the caller to cancel before playback actually begins (e.g. user
    // pauses Read Aloud during the minStartDelayMs window). `audio` is
    // assigned later; the closure captures the variable so the reference
    // stays valid whether or not we've reached playback yet.
    let cancelled = abort?.aborted ?? false;
    let audio: HTMLAudioElement | undefined;
    const handle: PlaybackHandle = {
      stop: () => {
        cancelled = true;
        if (audio && currentAudio === audio) stopCurrent();
        else audio?.pause();
      },
    };
    // Register synchronously so play.cancel() can reach in and abort before
    // speak() has finished fetching + setting up audio. Without this, rapid
    // skip-forward clicks queue multiple in-flight speak()s whose audio +
    // boundary polls overwrite each other's module-level state, leaking
    // setInterval timers that keep firing stale onBoundary callbacks.
    if (abort) abort.stop = handle.stop;

    // Flatten TextPart[] into a single string plus a lookup table for
    // textOffset → position. For string input, position tracking is a no-op.
    let flatText: string;
    const parts: BoundaryTextPart[] = [];
    if (typeof text === "string") {
      flatText = text;
    } else {
      let cursor = 0;
      flatText = "";
      for (const part of text) {
        parts.push({
          text: part.text,
          position: part.position,
          start: cursor,
          end: cursor + part.text.length,
        });
        flatText += part.text;
        cursor += part.text.length;
      }
    }

    let response: SynthesizeResponse;
    try {
      const result = await readlangClient.request(
        "POST",
        "/api/tts/synthesize",
        {
          text: flatText,
          language,
          voiceId: voice?.provider === PROVIDER_ID ? voice.id : undefined,
          feature,
          preview: opts.preview,
          timezoneOffset: new Date().getTimezoneOffset(),
          // Send rate to the server so Azure applies it via SSML <prosody>
          // at synthesis time — much higher quality than client-side
          // audio.playbackRate, which is a generic time-stretch with
          // audible artifacts (Azure docs cap rate to 0.5..2).
          rate: typeof rate === "number" ? rate : undefined,
        }
      );
      response = result.data as SynthesizeResponse;
    } catch (err) {
      if (!cancelled) {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      }
      return handle;
    }

    if (cancelled) return handle;

    // Mirror browserProvider: the caller resumes at the last part's position + 1.
    const nextPosition = (): number | undefined => {
      const lastPart = parts
        .slice()
        .reverse()
        .find((part) => part.position !== undefined);
      return lastPart ? (lastPart.position as number) + 1 : undefined;
    };

    // Azure answers 200 with an empty clip for text it has nothing to
    // pronounce. Handing that to the audio element produces an unplayable
    // `data:audio/mp3;base64,` source whose error would stop Read Aloud dead,
    // so report it as an utterance that finished instantly and let the caller
    // move on. Only when we know where to resume: without positions (previews,
    // Auto-Pronounce) empty audio is a genuine failure the caller must see.
    if (!response.audioBase64) {
      const resumePosition = nextPosition();
      if (resumePosition !== undefined) {
        onStart?.();
        onEnd?.(resumePosition);
        return handle;
      }
      onError?.(new Error("azure returned no audio"));
      return handle;
    }

    // Burn off whatever's left of the minimum start delay — fetch ran in
    // parallel with the pause, so this is often zero for warm Azure calls.
    if (typeof minStartDelayMs === "number" && minStartDelayMs > 0) {
      const remaining = minStartDelayMs - (Date.now() - t0);
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
      if (cancelled) return handle;
    }

    audio = getAudioElement();
    audio.src = `data:audio/mp3;base64,${response.audioBase64}`;
    // Rate is already baked into the synthesized audio by Azure (via SSML
    // <prosody>), so keep the element at native speed. Boundary timestamps
    // from the SDK also reflect the rate-adjusted audio, so the existing
    // currentTime-based polling lines up without conversion.
    audio.playbackRate = 1;
    audio.currentTime = 0;

    currentAudio = audio;

    // Schedule word-boundary callbacks by polling audio.currentTime against
    // the sorted boundary list. We only fire on WordBoundary — sentence and
    // punctuation boundaries are reported but not currently acted upon.
    const wordBoundaries = response.boundaries.filter(
      (boundary) => boundary.boundaryType === "WordBoundary"
    );
    let nextBoundaryIndex = 0;

    currentBoundaryPoll = window.setInterval(() => {
      const currentMs = audio.currentTime * 1000;
      while (
        nextBoundaryIndex < wordBoundaries.length &&
        wordBoundaries[nextBoundaryIndex].audioOffsetMs <= currentMs
      ) {
        if (parts.length > 0) {
          const position = boundaryOffsetToPosition(
            parts,
            wordBoundaries[nextBoundaryIndex].textOffset
          );
          if (position !== undefined) {
            onBoundary?.(position);
          }
        }
        nextBoundaryIndex += 1;
      }
    }, BOUNDARY_POLL_MS);

    audio.onended = () => {
      clearBoundaryPoll();
      if (currentAudio === audio) currentAudio = undefined;
      audio.onended = null;
      audio.onerror = null;
      // Fire remaining boundaries so the last word still highlights briefly.
      if (parts.length > 0) {
        while (nextBoundaryIndex < wordBoundaries.length) {
          const position = boundaryOffsetToPosition(
            parts,
            wordBoundaries[nextBoundaryIndex].textOffset
          );
          if (position !== undefined) {
            onBoundary?.(position);
          }
          nextBoundaryIndex += 1;
        }
      }
      onEnd?.(nextPosition());
    };

    audio.onerror = () => {
      stopCurrent();
      onError?.(new Error("azure audio playback failed"));
    };

    onStart?.();

    try {
      await audio.play();
    } catch (err) {
      stopCurrent();
      onError?.(err instanceof Error ? err : new Error(String(err)));
    }

    return handle;
  },
};
